import { useEffect, useMemo, useState } from "react";
import { LIBRARY_LIST_ID, fetchListPage, mapLibrary } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import type { GraphMeProfile } from "./useProfile";
import type { LibraryItem } from "../types/navigator";

export interface ForYouState {
  items: LibraryItem[];
  loading: boolean;
  error: string | null;
  /** True when scoring produced real matches; false when we're showing recent fallback. */
  personalized: boolean;
  /** Short string describing what we matched on, e.g. "Technology". */
  matchedOn: string | null;
  /** Library items ranked by Likes, most-liked first. Empty if nothing has been liked yet. */
  popularItems: LibraryItem[];
  /** Re-fetches the library page — call after an edit/like elsewhere so these rails stay in sync. */
  refetch: () => void;
}

// Common job-title noise words we don't want to use as match keywords.
const STOP_WORDS = new Set([
  "a", "an", "and", "or", "the", "of", "for", "to", "in", "at", "by", "with",
  "sr", "jr", "senior", "junior", "lead", "principal", "staff",
  "i", "ii", "iii", "iv",
]);

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w));
}

function scoreItem(item: LibraryItem, profile: GraphMeProfile): number {
  let score = 0;
  const dept = (profile.department || "").toLowerCase().trim();
  const itemDept = item.department.toLowerCase().trim();
  const haystack = `${item.title} ${item.description} ${item.department} ${item.benefitType}`.toLowerCase();

  // Department match — strongest signal.
  if (dept && itemDept) {
    if (itemDept === dept) score += 100;
    else if (itemDept.includes(dept) || dept.includes(itemDept)) score += 60;
  }

  // Job title keyword overlap.
  const titleWords = tokenize(profile.jobTitle || "");
  for (const word of titleWords) {
    if (haystack.includes(word)) score += 20;
  }

  // Recency bonus.
  const ts = new Date(item.modified || item.created || 0).getTime();
  if (ts > 0) {
    const daysOld = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    if (daysOld < 30) score += 8;
    else if (daysOld < 90) score += 4;
  }

  return score;
}

/**
 * Fetches Library items and ranks them against the user's Graph profile
 * (department + jobTitle). Returns the top matches; falls back to the most
 * recent items when scoring produces no signal (e.g., missing profile data).
 */
export function useForYou(
  profile: GraphMeProfile | null,
  limit = 5
): ForYouState {
  const getToken = useGraphToken();
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        // One page (50 items) is plenty for personalized matching on the
        // dashboard — keeps the request cheap.
        const { items } = await fetchListPage({
          token,
          listId: LIBRARY_LIST_ID,
          pageSize: 50,
        });
        if (cancelled) return;
        setLibrary(items.map(mapLibrary));
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, reloadKey]);

  const { items, personalized, matchedOn } = useMemo(() => {
    if (!library.length || !profile) {
      return { items: [] as LibraryItem[], personalized: false, matchedOn: null as string | null };
    }

    const scored = library
      .map((item) => ({ item, score: scoreItem(item, profile) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scored.length >= 2) {
      const dept = profile.department?.trim() || null;
      const title = profile.jobTitle?.trim() || null;
      const label = dept && title ? `${title} · ${dept}` : dept || title;
      return {
        items: scored.slice(0, limit).map((x) => x.item),
        personalized: true,
        matchedOn: label,
      };
    }

    // Fallback: show most recent library items so the rail still has value.
    const recent = library
      .slice()
      .sort((a, b) => {
        const ad = new Date(a.modified || a.created || 0).getTime();
        const bd = new Date(b.modified || b.created || 0).getTime();
        return bd - ad;
      })
      .slice(0, limit);

    return {
      items: recent,
      personalized: false,
      matchedOn: null,
    };
  }, [library, profile, limit]);

  const popularItems = useMemo(() => {
    return library
      .filter((item) => item.likes > 0)
      .slice()
      .sort((a, b) => b.likes - a.likes)
      .slice(0, limit);
  }, [library, limit]);

  return { items, loading, error, personalized, matchedOn, popularItems, refetch };
}
